import React, { useEffect } from 'react';
import { useState } from 'react';
import {
  Box,
  Text,
  VStack,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  HStack,
} from '@chakra-ui/react';
import { BaseBookReturn } from '../../../generated/client/models/BaseBookReturn';
import { CompleteBookReturn } from '../../../generated/client/models/CompleteBookReturn';
import { BooksService } from '../../../generated/client';
import { FaStar } from 'react-icons/fa';
import { CommentSection } from './CommentSection';
import saveAs from 'file-saver';

interface Props {
  book: BaseBookReturn;
  bookService: BooksService;
}

export function ResultsComponent({ book, bookService }: Props): JSX.Element {
  // setting all the setters and getters
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [completeBook, setCompleteBook] = useState<CompleteBookReturn>();
  const [avgRating, setAvgRating] = useState(0);
  const [isCommentsShown, setIsCommentsShown] = useState(false);

  // opening and closing the modal
  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // handling the star rating submission
  const handleStarSubmit = async () => {
    if (completeBook) {
      const bookRatings = completeBook.ratings ?? [];
      bookRatings.push(rating);
      const updatedBook: CompleteBookReturn = {
        id: completeBook.id,
        title: completeBook.title,
        authors: completeBook.authors,
        content: completeBook.content,
        comments: completeBook.comments ?? [],
        ratings: bookRatings,
      };
      setCompleteBook(updatedBook);
      await bookService.updateBook(updatedBook);
    }
  };

  // handling the star rating
  const handleStarClick = (selectedRating: number) => {
    if (selectedRating !== rating) {
      setRating(selectedRating);
    } else {
      setRating(0);
    }
  };

  // handling the comments section
  const handleOpenComments = () => {
    setIsCommentsShown(!isCommentsShown);
  };

  useEffect(() => {
    (async () => {
      if (completeBook?.ratings && completeBook.ratings.length != 0) {
        let totalRatingSum = 0;
        for (let i = 0; i < completeBook.ratings.length; i++) {
          totalRatingSum += completeBook.ratings[i];
        }
        setAvgRating(parseFloat((totalRatingSum / completeBook.ratings.length).toFixed(2)));
      } else setAvgRating(-1);
    })();
  }, [completeBook]);

  useEffect(() => {
    (async () => {
      const getCompleteBook = await bookService.getBook(book.id);
      setCompleteBook(getCompleteBook);
    })();
  }, [bookService, book.id]);

  // download the book as a txt file
  const downloadAsTxt = async () => {
    if (completeBook) {
      bookService
        .getBookText(book.id)
        .then(response => {
          const filename = `${completeBook.title}.txt`;
          const fileBlob = new Blob([response], { type: 'text/plain;charset=utf-8' });
          saveAs(fileBlob, filename);
        })
        .catch(error => {
          console.error('Error getting book text:', error);
        });
    }
  };

  return (
    <div>
      <Box className='resultsWrap'>
        <VStack spacing={5} align='start'>
          <Text fontSize='2xl' fontWeight='bold'>
            {book.title}
          </Text>
          <Text fontSize='md'>
            {book.authors.map((author: { name: unknown }) => author.name).join(', ')}
          </Text>
          {/* adds ability the view the book */}
          <Button colorScheme='teal' onClick={openModal}>
            View Book
          </Button>
          {/* add the download buttons */}
          <Button colorScheme='teal' onClick={downloadAsTxt}>
            Download as TXT
          </Button>
          <HStack>
            {[1, 2, 3, 4, 5].map(index => (
              <Box key={index} aria-label={`${index} star${index === 1 ? '' : 's'}`}>
                <FaStar color={index <= Math.round(avgRating) ? 'gold' : 'gray'} />
              </Box>
            ))}
            {/* adds the ratings to the book */}
            <Text fontSize='md'>{`${
              avgRating == -1 ? 'No ratings' : avgRating + ' star' + `${rating === 1 ? '' : 's'}`
            }`}</Text>
          </HStack>
          <Text>{}</Text>
          <Modal isOpen={isModalOpen} onClose={closeModal}>
            <ModalOverlay />
            {/* adds the book viewing modal */}
            <ModalContent maxW='56rem'>
              <ModalHeader>
                {book.title}
                <VStack>
                  <HStack>
                    {[1, 2, 3, 4, 5].map(index => (
                      <Box
                        key={index}
                        onClick={() => handleStarClick(index)}
                        aria-label={`${index} star${index === 1 ? '' : 's'}`}>
                        <FaStar color={index <= rating ? 'gold' : 'gray'} />
                      </Box>
                    ))}
                    <Text fontSize='md'>{`${rating} star${rating === 1 ? '' : 's'}`}</Text>
                  </HStack>
                  <Button colorScheme='teal' onClick={handleStarSubmit}>
                    Rate
                  </Button>
                  <Button colorScheme='teal' onClick={handleOpenComments}>
                    View Comments
                  </Button>
                  <HStack>
                    <Text fontSize='md'>Average Stars:</Text>
                    {[1, 2, 3, 4, 5].map(index => (
                      <Box key={index} aria-label={`${index} star${index === 1 ? '' : 's'}`}>
                        <FaStar color={index <= Math.round(avgRating) ? 'gold' : 'gray'} />
                      </Box>
                    ))}
                    <Text fontSize='md'>{avgRating == -1 ? 'No ratings' : avgRating}</Text>
                  </HStack>
                </VStack>
              </ModalHeader>
              <ModalCloseButton />
              {completeBook && completeBook.content ? (
                <ModalBody className='horizontalWrap'>
                  <iframe
                    title={book.title}
                    src={completeBook.content}
                    style={{ width: '100%', height: '500px' }}
                  />
                  {isCommentsShown ? (
                    <Box className='resultsWrap'>
                      <CommentSection
                        key={completeBook.id}
                        completeReturn={completeBook}
                        service={bookService}></CommentSection>
                    </Box>
                  ) : (
                    ''
                  )}
                </ModalBody>
              ) : (
                <ModalBody> No book found </ModalBody>
              )}
            </ModalContent>
          </Modal>
        </VStack>
      </Box>
    </div>
  );
}
